import styled, { css } from 'styled-components';

import { baseContentStyling } from './HomepageWrapper.base.styles';
import { media } from '../shared/core/styles';

/**
 * AAF renders the shared structure only — no brand-specific values yet. Add
 * AAF deltas here when its homepage design lands; until then the base var
 * fallbacks apply.
 */
export const Wrapper = styled.div`
  ${baseContentStyling}

  ${({ theme }) => css`
    /* AAF default; base ColorSchemes on .contentBlock override it */
    --contentBlock-text-color: ${theme.colors.neutral['900']};

    /* Heading */
    --heading-font-family: var(--font-body);
    --heading-font-size: ${theme.font.size[4]};
    --heading-font-weight: ${theme.variables.font.weight.bold};
    --heading-line-height: 1.2em;
    --heading-letter-spacing: 0.05em;

    /* Body */
    --body-font-family: var(--font-body);
    --body-font-size: ${theme.font.size[3]};
    --body-line-height: 1.35em;
    --body-letter-spacing: calc(var(--body-font-size) / 100);

    [class*='row--aaf'] {
      margin-bottom: 2px;

      .contentBlock {
        &-body {
          overflow: visible !important;

          &Align {
            position: relative;
            display: flex;
            width: 100%;
            height: 100%;
            flex-direction: column;
            align-items: var(--contentBlock-justify-content, flex-start);
            justify-content: var(--contentBlock-align-items, center);
          }

          &-title,
          &-subTitle {
            font-family: var(--heading-font-family);
            font-size: var(--title-font-size, var(--heading-font-size));
            font-weight: var(--heading-font-weight);
            line-height: var(--heading-line-height);
            letter-spacing: var(--heading-letter-spacing);
            text-transform: uppercase;

            &:not(:last-child) {
              margin-bottom: 1em;
            }
          }

          &-subTitle {
            font-size: var(--heading-font-size);
          }

          &-text {
            margin: 0;
            font-family: var(--body-font-family);
            font-size: var(--body-font-size);
            letter-spacing: var(--body-letter-spacing);

            &,
            * {
              line-height: var(--body-line-height);
            }
          }
        }
      }
    }

    .row--aaf-hero,
    .row--aaf-secondary {
      /* AAF default; base ButtonVariants on .contentBlock override it */
      --contentBlock-button-gap: ${theme.spacing.x['2xs']};
      --contentBlock-body-padding: ${theme.size['8']};
      --contentBlock-button-margin: 0;

      .contentBlock {
        --title-font-size: ${theme.font.size[7]};

        &-body {
          &-links {
            position: absolute;
            inset: ${theme.spacing.x.lg};
            justify-content: var(--contentBlock-justify-content, flex-start);
            align-items: flex-end;

            @media ${media(theme).greaterThan('sm')} {
              inset: ${theme.size['8']};
            }
          }
        }

        /* Hovering the block shows each button's own variant hover state. :active
           is excluded so pressing a button still shows its active state */
        @media ${media(theme).canHover} {
          /* OUTLINE (default) */
          &:is(:hover, :focus-within) :where(.contentBlock-body-link:not(:active)) {
            --contentBlock-button-border-color: var(
              --contentBlock-button-hover-border-color,
              ${theme.colors.neutral['900']}
            );
            --contentBlock-button-background-color: var(
              --contentBlock-button-hover-background-color,
              ${theme.colors.neutral['900']}
            );
            --contentBlock-button-text-color: var(
              --contentBlock-button-hover-text-color,
              ${theme.colors.neutral['0']}
            );
          }

          /* SOLID */
          &:where(.contentBlock--buttonSolid, [data-button-type='solid']):is(:hover, :focus-within)
            :where(.contentBlock-body-link:not(:active)),
          &:where([data-button-solid='1']):is(:hover, :focus-within)
            :where(.contentBlock-body-link:first-child:not(:active)),
          &:where([data-button-solid='2']):is(:hover, :focus-within)
            :where(.contentBlock-body-link:nth-child(2):not(:active)) {
            --contentBlock-button-border-color: var(
              --contentBlock-buttonSolid-hover-border-color,
              ${theme.colors.neutral['900']}
            );
            --contentBlock-button-background-color: var(
              --contentBlock-buttonSolid-hover-background-color,
              transparent
            );
            --contentBlock-button-text-color: var(
              --contentBlock-buttonSolid-hover-text-color,
              ${theme.colors.neutral['900']}
            );
          }

          /* UNDERLINE + LINK */
          &:where(
              .contentBlock--buttonUnderline,
              .contentBlock--buttonLink,
              [data-button-type='underline'],
              [data-button-type='link']
            ):is(:hover, :focus-within)
            :where(.contentBlock-body-link:not(:active)) {
            --contentBlock-button-text-color: var(
              --contentBlock-buttonText-hover-color,
              ${theme.colors.slate['600']}
            );
          }

          &:where(
              .contentBlock--buttonLink,
              .contentBlock--buttonUnderline,
              [data-button-type='link'],
              [data-button-type='underline']
            ):is(:hover, :focus-within)
            :where(.contentBlock-body-link) {
            --underline-width: 2px;
          }
        }
      }
    }

    .row[class*='row--aaf'] {
      [data-image-focus='top'] {
        .contentBlock-picture-image {
          object-position: 50% 20%;
        }
      }

      [data-image-focus='bottom'] {
        .contentBlock-picture-image {
          object-position: 50% 80%;
        }
      }
    }

    .row--aaf-hero {
      --contentBlock-body-padding: ${theme.size['8']} ${theme.spacing.x.md};

      padding: 0;
      width: 100%;
      height: calc(100svh - var(--top-nav-height, ${theme.variables.topNavHeight}));
      max-height: calc(100vw * 16 / 9 - var(--top-nav-height, ${theme.variables.topNavHeight}));

      @media ${media(theme).greaterThan('sm')} {
        --contentBlock-body-padding: ${theme.size['8']} ${theme.spacing.x.lg};

        height: auto;
        max-height: none;
        aspect-ratio: 16/9;
      }

      /* Multi-column heroes overlay their columns; base only stacks a lone .col */
      &:not(.row--sticky) .col {
        grid-area: 1 / 1;
      }

      &.row--sticky {
        overflow: visible;
        display: block;
        gap: 0;

        .col--main {
          position: absolute;
          inset: 0;
        }

        .col--text {
          position: sticky;
          inset: var(--top-nav-height) 0 0;
          height: calc(100vh - var(--top-nav-height));
          max-height: 100%;
        }
      }

      .contentBlock-body {
        position: absolute;
        inset: 0;

        &Align {
          justify-content: var(--contentBlock-align-items, flex-end);
        }

        &-title {
          height: calc(100% / 3);
          margin-bottom: 0;
        }

        &-links {
          justify-content: var(--contentBlock-justify-content, center);
          bottom: ${theme.size['8']};
        }
      }
    }

    .row--aaf-secondary {
      display: grid;
      gap: 2px;
      grid-template-columns: 1fr;
      grid-template-rows: auto;

      @media ${media(theme).greaterThan('sm')} {
        grid-template-columns: repeat(6, 1fr);
      }

      .col {
        position: relative;
        width: auto;
        margin: 0;

        @media ${media(theme).greaterThan('sm')} {
          grid-column: auto / span 3;
        }

        /* spread */
        &:first-child:last-child {
          /* AAF default; base Layout on .contentBlock overrides it */
          --contentBlock-justify-content: center;

          grid-column: auto / span 6;

          @media ${media(theme).greaterThan('sm')} {
            .contentBlock:first-child {
              aspect-ratio: 16/9;
            }
          }
        }

        /* image */
        .contentBlock:first-child {
          aspect-ratio: 4/5;

          @media ${media(theme).greaterThan('sm')} {
            height: calc(100vh - var(--top-nav-height));
            aspect-ratio: auto;
          }

          @media ${media(theme).greaterThan('sm')} and (min-aspect-ratio: 10/5.3) {
            aspect-ratio: 5/5;
            height: auto;
          }

          @media ${media(theme).greaterThan('sm')} and (max-aspect-ratio: 8/5.2) {
            aspect-ratio: 4/5;
            height: auto;
          }
        }

        /* text only */
        .contentBlock-body:first-child {
          --contentBlock-body-padding: ${theme.spacing.x.lg};

          position: static;
          min-width: 380px;
          width: 90%;
          margin: 0;

          @media ${media(theme).greaterThan('sm')} {
            --contentBlock-body-padding: ${theme.size['8']};
          }

          .contentBlock-bodyAlign {
            text-align: var(--contentBlock-text-align, left);
          }

          .contentBlock-body-link {
            position: absolute;
            inset: 0;
            opacity: 0;
          }
        }
      }

      .contentBlock {
        .contentBlock-picture {
          &-image {
            transform: scale(1.01);
            transition: transform 0.6s cubic-bezier(0.77, 0, 0.175, 1);

            @media (prefers-reduced-motion) {
              transition: none;
            }
          }
        }

        @media ${media(theme).canHover} {
          &:is(:hover, :focus-within) {
            .contentBlock-picture {
              &-image {
                transform: scale(1.03);
              }
            }
          }
        }
      }

      &.row--aaf-textOnly {
        .col {
          .contentBlock {
            aspect-ratio: auto;
            height: auto;
          }
        }

        @media ${media(theme).lessThan('sm')} {
          gap: 0;

          .col:nth-child(2) .contentBlock-bodyAlign {
            padding-top: 0 !important;
          }
        }
      }
    }

    .row--aaf-categories {
      &[data-margin] {
        margin-block: ${theme.spacing.y['2xl']};
      }

      .col {
        /* AAF default; base ColorSchemes on .contentBlock override it */
        --contentBlock-text-color: ${theme.colors.neutral['0']};
        --contentBlock-body-padding: ${theme.spacing.x.md};
        --contentBlock-button-margin: 0;

        position: relative;
        width: 100%;

        .contentBlockList {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-template-rows: auto;
          gap: 2px;

          @media ${media(theme).lessThan('sm')} {
            .contentBlock {
              grid-column: auto / span var(--span, 1);

              &:first-child:nth-last-child(2n + 3) {
                --span: 2;
              }
            }
          }

          @media ${media(theme).greaterThan('sm')} {
            grid-template-columns: repeat(60, 1fr);

            .contentBlock {
              grid-column: auto / span var(--span, 15);

              /* 2 Column */
              &:first-child:nth-last-child(2),
              &:last-child:nth-child(2) {
                --span: 30;
              }

              /* 3 Columns */
              &:first-child:nth-last-child(3n) {
                &,
                ~ .contentBlock {
                  --span: 20;
                }
              }

              /* 4 Columns */
              &:first-child:nth-last-child(4n) {
                &,
                ~ .contentBlock {
                  --span: 15;
                }
              }

              /* 5 Columns */
              &:first-child:nth-last-child(5n) {
                &,
                ~ .contentBlock {
                  --span: 12;
                }
              }
            }
          }
        }

        .contentBlock {
          aspect-ratio: 4/5;
          overflow: hidden;

          .contentBlock-picture {
            &-image {
              transform: scale(1.01);
              transition: transform 0.6s cubic-bezier(0.77, 0, 0.175, 1);

              @media (prefers-reduced-motion) {
                transition: none;
              }
            }
          }

          @media ${media(theme).canHover} {
            &:is(:hover, :focus-within) {
              --label-offset: -2px;

              .contentBlock-picture {
                &-image {
                  transform: scale(1.03);
                }
              }
            }
          }

          &:has(.contentBlock-body-link:focus-visible)::after {
            content: '';
            position: absolute;
            inset: ${theme.spacing.x.xs};
            z-index: var(--contentBlock-button-z-index, 20);
            pointer-events: none;
            outline: 2px auto Highlight;
            outline: 2px auto -webkit-focus-ring-color;
          }

          &-body {
            position: absolute;
            inset: 0;

            &Align {
              justify-content: var(--contentBlock-align-items, flex-end);
            }

            &-title {
              margin: 0;
              font-size: ${theme.font.size[3]};
              transition: transform 0.4s cubic-bezier(0.77, 0, 0.175, 1);
              transform: translateY(var(--label-offset, 0));

              @media (prefers-reduced-motion) {
                transition: none;
              }
            }

            &-link {
              position: absolute;
              inset: ${theme.spacing.x.xs};
              opacity: 0;
            }
          }
        }
      }
    }

    .row--aaf-products {
      display: flex;
      flex-direction: column;

      .col {
        position: relative;
        width: 100%;
      }

      .contentBlockList {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto;
        gap: 2px;

        @media ${media(theme).greaterThan('sm')} {
          grid-template-columns: repeat(60, 1fr);

          > * {
            grid-column: auto / span var(--span, 15);

            /* 3 Columns */
            &:first-child:nth-last-child(3n) {
              &,
              ~ * {
                --span: 20;
              }
            }

            /* 4 Columns */
            &:first-child:nth-last-child(4n) {
              &,
              ~ * {
                --span: 15;
              }
            }
          }
        }
      }

      .col--text {
        /* AAF default; base Layout on .contentBlock overrides it */
        --contentBlock-justify-content: center;
        --contentBlock-body-padding: ${theme.spacing.y.lg} ${theme.spacing.x.lg} ${theme.size['8']};

        @media ${media(theme).greaterThan('sm')} {
          --contentBlock-body-padding: ${theme.size['8']} ${theme.spacing.x.lg}
            ${theme.spacing.y['2xl']};
        }
      }
    }
  `}
`;
